import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Search } from "lucide-react";
import { formatDateES } from "@/lib/utils-app";

// V1.82 · Contabilidad · Ventas pendientes: alumnos con facturas de venta
// marcadas como «FALTA FACTURA» (no emitidas). Fuente: facturas del alumno.
export default function PendingSalesPanel() {
  const [data, setData] = useState({ rows: [], total_invoices: 0, total_students: 0, total_classes: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/accounting/pending-sales-invoices");
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.rows || [];
    return (data.rows || []).filter((r) =>
      `${r.student_name} ${r.codigo_expediente} ${r.number} ${r.notes}`.toLowerCase().includes(q));
  }, [data.rows, query]);

  return (
    <div className="space-y-4" data-testid="pending-sales-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Facturas pendientes" value={data.total_invoices} testId="pending-sales-total-invoices" />
        <StatCard label="Alumnos afectados" value={data.total_students} testId="pending-sales-total-students" />
        <StatCard label="Clases sin facturar" value={data.total_classes} testId="pending-sales-total-classes" />
      </div>

      <div className="card-surface p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <h3 className="font-bold">Facturas de venta pendientes</h3>
          <span className="text-xs text-neutral-500">
            Marcadas desde la ficha del alumno con «FALTA FACTURA» o sin emitir
          </span>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar alumno o nº…"
                data-testid="pending-sales-search"
                className="rounded-lg border border-neutral-300 bg-white pl-8 pr-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <button onClick={load} data-testid="pending-sales-reload" className="btn-secondary !h-9 !text-xs">
              <RefreshCw className="h-3.5 w-3.5" /> Actualizar
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 py-4">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50 p-6 text-center text-sm text-emerald-800 font-semibold"
            data-testid="pending-sales-empty">
            No hay facturas de venta pendientes. Todo facturado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 pr-3">Alumno</th>
                  <th className="py-2 pr-3">Expediente</th>
                  <th className="py-2 pr-3">Nº factura</th>
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3 text-right">Clases</th>
                  <th className="py-2">Nota</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.map((r) => (
                  <tr key={r.invoice_id} data-testid={`pending-sales-row-${r.invoice_id}`} className="hover:bg-amber-50/50">
                    <td className="py-2 pr-3 font-semibold text-neutral-900">
                      {r.student_name || "—"}
                      {r.active === false && (
                        <span className="ml-1.5 text-[10px] uppercase text-neutral-400">inactivo</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-neutral-600">{r.codigo_expediente || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.number || <span className="text-amber-700 font-semibold">sin nº</span>}</td>
                    <td className="py-2 pr-3 text-xs text-neutral-600">{r.date ? formatDateES(r.date) : "—"}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.classes_included}</td>
                    <td className="py-2 text-xs italic text-neutral-500">{r.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500">{label}</div>
      <div className="text-2xl font-bold font-mono tabular-nums text-neutral-900">{value ?? 0}</div>
    </div>
  );
}
